/**
 * Loads Apple MapKit JS and initialises it with a token fetched from the
 * backend (`/mapkit/token`). Resolves to the global `mapkit` object, or `null`
 * when Apple Maps isn't configured (no token) — callers then fall back to the
 * Leaflet map.
 *
 * The whole thing is memoised so the script loads once per page.
 */
import { BASE_URL } from '../redux/baseQuery';

const MAPKIT_SRC = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MapKit = any;

let promise: Promise<MapKit | null> | null = null;

const injectScript = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (document.getElementById('apple-mapkit-js')) return resolve();
    const el = document.createElement('script');
    el.id = 'apple-mapkit-js';
    el.src = MAPKIT_SRC;
    el.crossOrigin = 'anonymous';
    el.onload = () => resolve();
    el.onerror = () => reject(new Error('mapkit.js failed to load'));
    document.head.appendChild(el);
  });

export function loadMapKit(): Promise<MapKit | null> {
  if (promise) return promise;
  promise = (async () => {
    let token: string | null = null;
    try {
      const res = await fetch(`${BASE_URL}/mapkit/token`);
      const data = (await res.json()) as { token: string | null };
      token = data.token;
    } catch {
      return null;
    }
    if (!token) return null;

    try {
      await injectScript();
    } catch {
      return null;
    }

    const w = window as unknown as { mapkit?: MapKit };
    if (!w.mapkit) return null;

    // init() takes an authorizationCallback that supplies the token. We keep
    // it simple with the token we already fetched; MapKit refreshes via the
    // same callback when needed.
    await new Promise<void>((resolve) => {
      w.mapkit!.init({
        authorizationCallback: (done: (t: string) => void) => done(token as string),
      });
      resolve();
    });

    return w.mapkit;
  })();
  return promise;
}
